import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Eye, EyeOff, Key, Plug, Settings as SettingsIcon, X, ExternalLink } from 'lucide-react';
import { useUIStore } from '@/lib/store/uiStore';
import { ProviderConnections } from './ProviderConnections';

interface KeyField {
  id: 'openai' | 'anthropic' | 'gemini';
  label: string;
  placeholder: string;
  helpUrl: string;
  note?: string;
  disabled?: boolean;
}

const FIELDS: KeyField[] = [
  {
    id: 'openai',
    label: 'OpenAI',
    placeholder: 'sk-…',
    helpUrl: 'https://platform.openai.com/api-keys',
    note: 'Used for GPT-5, GPT-5 mini, o4. Calls go directly from your browser to api.openai.com.',
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    placeholder: 'sk-ant-…',
    helpUrl: 'https://console.anthropic.com/settings/keys',
    note: 'Disabled in browser mode — Anthropic API blocks browser CORS. Will work once Xoro runs inside the Tauri shell.',
    disabled: true,
  },
  {
    id: 'gemini',
    label: 'Google Gemini',
    placeholder: 'AIza…',
    helpUrl: 'https://aistudio.google.com/app/apikey',
    note: 'Adapter not yet implemented (planned for next phase).',
    disabled: true,
  },
];

export function SettingsDialog() {
  const open = useUIStore((s) => s.settingsOpen);
  const setOpen = useUIStore((s) => s.setSettingsOpen);
  const apiKeys = useUIStore((s) => s.apiKeys);
  const setApiKey = useUIStore((s) => s.setApiKey);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/15 animate-fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-canvas shadow-menu animate-fade-in"
          aria-describedby={undefined}
        >
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <SettingsIcon className="h-4 w-4 text-fg-muted" />
            <Dialog.Title className="text-[14px] font-semibold text-fg">
              Settings
            </Dialog.Title>
            <Dialog.Close className="focus-ring ml-auto rounded p-1 text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label="Close">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="max-h-[calc(100vh-180px)] overflow-y-auto px-5 py-4">
            <div className="mb-3 flex items-center gap-1.5 text-2xs uppercase tracking-wide text-fg-subtle">
              <Plug className="h-3 w-3" />
              <span>Provider connections</span>
            </div>
            <ProviderConnections />
            <p className="mt-3 rounded-md border border-border bg-surface px-3 py-2 text-[12px] leading-relaxed text-fg-muted">
              CLI sessions sign in via OAuth on the VPS. Click <em>Connect</em>, copy the
              URL it shows, sign in in your browser, paste the verification code back.
              Credentials live in the <code className="font-mono">ubuntu</code> user's home
              directory and are reused by every Xoro session.
            </p>

            <div className="mb-3 mt-6 flex items-center gap-1.5 text-2xs uppercase tracking-wide text-fg-subtle">
              <Key className="h-3 w-3" />
              <span>Provider API keys</span>
            </div>

            <div className="space-y-3">
              {FIELDS.map((f) => (
                <KeyRow
                  key={f.id}
                  field={f}
                  value={apiKeys[f.id] ?? ''}
                  onChange={(v) => setApiKey(f.id, v)}
                />
              ))}
            </div>

            <p className="mt-4 rounded-md border border-border bg-surface px-3 py-2 text-[12px] leading-relaxed text-fg-muted">
              Keys are stored in <code className="font-mono">localStorage</code> on this
              machine. Nothing is sent to a Xoro server — provider calls go straight
              from your browser to the provider's API.
            </p>
          </div>

          <div className="flex items-center gap-2 border-t border-border px-5 py-3">
            <span className="text-2xs text-fg-subtle">Changes apply immediately.</span>
            <Dialog.Close className="focus-ring ml-auto rounded-md bg-fg px-3 py-1.5 text-[12.5px] font-medium text-canvas hover:bg-fg/90">
              Done
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function KeyRow({
  field, value, onChange,
}: {
  field: KeyField;
  value: string;
  onChange: (v: string) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-2">
        <label className="text-[13px] font-medium text-fg">{field.label}</label>
        {field.disabled && (
          <span className="rounded bg-surface-2 px-1.5 py-0.5 text-2xs font-medium text-fg-subtle">
            Disabled
          </span>
        )}
        {!field.disabled && value && (
          <span className="rounded bg-success/10 px-1.5 py-0.5 text-2xs font-medium text-success">
            Configured
          </span>
        )}
        <a
          href={field.helpUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto inline-flex items-center gap-0.5 text-2xs text-fg-muted hover:text-fg"
        >
          Get key
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={field.disabled}
          spellCheck={false}
          autoComplete="off"
          className="focus-ring w-full rounded-md border border-border bg-canvas px-2.5 py-1.5 font-mono text-[12.5px] text-fg outline-none placeholder:text-fg-subtle disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          disabled={field.disabled}
          className="focus-ring rounded p-1 text-fg-subtle hover:text-fg disabled:opacity-30"
          aria-label={show ? 'Hide key' : 'Show key'}
        >
          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>
      {field.note && (
        <p className="mt-1.5 text-2xs leading-relaxed text-fg-muted">{field.note}</p>
      )}
    </div>
  );
}
